import {
  HeaderSkeleton,
  Line,
  PanelSkeleton,
  SkeletonRegion,
  TableSkeleton,
} from '@ems/ui/patterns/skeleton-blocks'

export function ApplicationsSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-32" description="w-36" />
      <TableSkeleton
        card={{ title: 'w-28', description: null }}
        columns={[
          { cell: 'mono', widths: ['w-24', 'w-20'], primary: true },
          { cell: 'text', widths: ['w-40', 'w-48', 'w-32'] },
          { cell: 'badge', widths: ['w-24', 'w-20'] },
          { cell: 'muted', widths: ['w-24', 'w-32'] },
          { cell: 'text', widths: ['w-8', 'w-6'], align: 'end' },
        ]}
      />
    </SkeletonRegion>
  )
}

export function FloorSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-20" description="w-48" />
      <TableSkeleton
        card={{ title: 'w-36', description: null }}
        columns={[
          { cell: 'badge', widths: ['w-20', 'w-24'], primary: true },
          { cell: 'muted', widths: ['w-32', 'w-24'] },
          { cell: 'text', widths: ['w-8'], align: 'end' },
          { cell: 'text', widths: ['w-16', 'w-12'], align: 'end' },
        ]}
      />
    </SkeletonRegion>
  )
}

/** One application: the header, the status and company cards, then the stands. */
export function ApplicationSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-56" description="w-72" actions={['h-9 w-36', 'h-9 w-32']} />
      <div className="grid gap-6 md:grid-cols-2">
        <PanelSkeleton title="w-16">
          <Line width="w-40" />
          <Line width="w-48" />
          <Line width="w-44" />
        </PanelSkeleton>
        <PanelSkeleton title="w-44">
          <Line width="w-52" />
          <Line width="w-40" />
          <Line width="w-48" />
        </PanelSkeleton>
      </div>
      <TableSkeleton
        rows={2}
        card={{ title: 'w-16', description: 'w-14' }}
        columns={[
          { cell: 'mono', widths: ['w-14', 'w-12'], primary: true },
          { cell: 'text', widths: ['w-8'] },
          { cell: 'text', widths: ['w-12'], align: 'end' },
          { cell: 'text', widths: ['w-24'], align: 'end' },
          { cell: 'badge', widths: ['w-20'] },
        ]}
      />
    </SkeletonRegion>
  )
}

export function ServiceOrdersSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-36" description="w-40" />
      <PanelSkeleton title="w-28">
        <Line width="w-72" />
        <Line width="w-96" />
      </PanelSkeleton>
      <TableSkeleton
        card={{ title: 'w-16', description: null }}
        columns={[
          { cell: 'text', widths: ['w-40', 'w-48', 'w-32'], primary: true },
          { cell: 'badge', widths: ['w-20', 'w-24'] },
          { cell: 'muted', widths: ['w-16', 'w-14'] },
          { cell: 'muted', widths: ['w-32', 'w-28'] },
          { cell: 'text', widths: ['w-24', 'w-20'], align: 'end' },
        ]}
      />
    </SkeletonRegion>
  )
}

/** One service order: the header, the order and totals cards, then the services. */
export function ServiceOrderSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-56" description="w-48" actions={['h-9 w-32']} />
      <div className="grid gap-6 md:grid-cols-2">
        <PanelSkeleton title="w-14">
          <Line width="w-20" />
          <Line width="w-48" />
          <Line width="w-40" />
        </PanelSkeleton>
        <PanelSkeleton title="w-14">
          <Line width="w-52" />
          <Line width="w-44" />
          <Line width="w-48" />
        </PanelSkeleton>
      </div>
      <TableSkeleton
        rows={2}
        card={{ title: 'w-16', description: 'w-16' }}
        columns={[
          { cell: 'text', widths: ['w-32', 'w-40'], primary: true },
          { cell: 'text', widths: ['w-8'], align: 'end' },
          { cell: 'text', widths: ['w-24'], align: 'end' },
          { cell: 'text', widths: ['w-10'], align: 'end' },
          { cell: 'text', widths: ['w-24'], align: 'end' },
        ]}
      />
    </SkeletonRegion>
  )
}

/** The onboarding desk: the numbered sections on the left, the order summary on the right. */
export function OnboardingSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-48" description="w-96" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="flex flex-col gap-6">
          <PanelSkeleton title="w-44" description="w-80">
            <Line width="w-full" />
            <Line width="w-3/4" />
          </PanelSkeleton>
          <PanelSkeleton title="w-20" description="w-72">
            <Line width="w-full" />
            <Line width="w-2/3" />
            <Line width="w-1/2" />
          </PanelSkeleton>
          <PanelSkeleton title="w-36" description="w-64">
            <Line width="w-full" />
            <Line width="w-3/4" />
          </PanelSkeleton>
        </div>
        <PanelSkeleton title="w-32">
          <Line width="w-40" />
          <Line width="w-48" />
          <Line width="w-full" />
        </PanelSkeleton>
      </div>
    </SkeletonRegion>
  )
}
